import React, { useMemo, useState } from 'react'
import theme from '../../theme/theme'
import { Api } from '../../network/api'
import { ChevaronDownArrowIcon,PaperIcon } from '../../assets/icons'
import BASE_URL from '../../network/config'
import { handleS3Download } from '../../utils/utils'


const PastPaperList = () => {
  const [subjectFilter, setSubjectFilter] = useState('all')
  const [pastPapers, setPastPaper] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [percentageFilter, setPercentageFilter] = useState('all')

  const fetchPastModalAnswers = async () => {
    setIsLoading(true);
    try {
      const response = await Api.getPastPapers();
      const data = response.data;
      if (data) {
        setPastPaper(data.past_model_answers);
        setSubjectFilter(data.past_model_answers.length > 0 ? data.past_model_answers[0].subject_name : 'all');
      } else {
        throw new Error("No data received from API");
      }
    } catch (apiError) {
      console.error("Error fetching past modal answers:", apiError);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    fetchPastModalAnswers();
  }, []);

  const subjects = useMemo(() => {
    const set = new Set()
    pastPapers.forEach(p => set.add(p.subject_name))
    return Array.from(set).sort()
  }, [pastPapers])

   const getPercentageRange = (value) => {
    let num = typeof value === 'string' && value.startsWith('<') ? parseInt(value.replace('<', '')) - 1 : parseInt(value)
    if (isNaN(num)) return '<60'
    if (num >= 90) return '90+';
    if (num >= 80) return '80-89';
    if (num >= 70) return '70-79';
    if (num >= 60) return '60-69';
    return '<60';
  }

  const percentageOptions = [
    { value: 'all', label: 'All' },
    { value: '90+', label: '90% and above' },
    { value: '80-89', label: '80% - 89%' },
    { value: '70-79', label: '70% - 79%' },
    { value: '60-69', label: '60% - 69%' },
    { value: '<60', label: 'Below 60%' },
  ]

  const filteredAndRanked = useMemo(() => {
    return pastPapers
      .map(paper => {
        const subjectMatches = subjectFilter === 'all' || paper.subject_name === subjectFilter
        const percentageMatches = percentageFilter === 'all' || getPercentageRange(paper.grade) === percentageFilter
        const matchScore = (subjectMatches ? 1 : 0) + (percentageMatches ? 1 : 0)
        return { paper, matchScore, subjectMatches, percentageMatches }
      })
      .filter(item => item.subjectMatches && item.percentageMatches)
      .sort((a, b) => {
        if (b.matchScore !== a.matchScore) return b.matchScore - a.matchScore
        const gradeA = typeof a.paper.grade === 'string' && a.paper.grade.startsWith('<') ? parseInt(a.paper.grade.replace('<', '')) - 1 : parseInt(a.paper.grade)
        const gradeB = typeof b.paper.grade === 'string' && b.paper.grade.startsWith('<') ? parseInt(b.paper.grade.replace('<', '')) - 1 : parseInt(b.paper.grade)
        if (gradeB !== gradeA) return gradeB - gradeA
        return (b.paper.year || 0) - (a.paper.year || 0)
      })
      .map(item => item.paper)
  }, [subjectFilter, percentageFilter, pastPapers])

  return (
    <main className=''>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Past Model Papers</h1>
                <p className="text-gray-600 mt-1">Browse and open past model papers</p>
              </div>
              <div className="flex flex-col md:flex-row items-center md:items-end gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 text-left mb-1">Subject</label>
                  <div className="relative">
                    <select
                      value={subjectFilter}
                      onChange={(e) => setSubjectFilter(e.target.value)}
                      className="block w-56 md:w-64 appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    >
                      <option value="all">All</option>
                      {subjects.map((subj) => (
                        <option key={subj} value={subj}>{subj}</option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">
                   <ChevaronDownArrowIcon/>
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 text-left mb-1">Percentage</label>
                  <div className="relative">
                    <select
                      value={percentageFilter}
                      onChange={(e) => setPercentageFilter(e.target.value)}
                      className="block w-48 md:w-56 appearance-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    >
                      {percentageOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">
                      <ChevaronDownArrowIcon />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Year</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">File Name</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAndRanked?.map((paper) => (
                  <tr key={paper.id} className="hover:bg-gray-50 transition-colors duration-200">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {paper?.subject_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {paper?.year}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      <span className="truncate inline-block max-w-[240px]" title={paper?.original_filename}>{paper?.original_filename}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                        {paper?.grade}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <button
                         onClick={() => handleS3Download(paper.s3_url, BASE_URL)}
                         style={{ backgroundColor: theme.Colors.primary }}
                                                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white hover:opacity-90 transition-opacity duration-200"
                        >
                        View
                      </button>
                    </td>
                  </tr>
                ))} 
              </tbody>
            </table>
          </div>
          {filteredAndRanked.length === 0 && (
            <div className="text-center py-12">
            <PaperIcon />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No past papers</h3>
              <p className="mt-1 text-sm text-gray-500">Adjust your filters to see matching past model papers.</p>
            </div>
          )}
        </div>
      </div>
    </main>
  )
}

export default PastPaperList